import { Box, Button, ButtonGroup, Card, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../app/AppStyles';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../subcomponents/alertSlice';
import AutocompleteSelector from '../subcomponents/AutocompleteSelector';
import ContentLoader from '../subcomponents/ContentLoader';
import { fetchTestUsersForLink, putLinkUsers, putUnlinkUsers, resetEmployeeStatus } from './employeeSlice';

import LinkOffIcon from '@mui/icons-material/LinkOff';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: 'auto' },
    maxHeight: '90%',
    boxShadow: 24,
};

export default function EmployeeLinkCard({ selectedEmployee, handleClose }) {
    const dispatch = useDispatch();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { testUsers, linkUsers, unlinkUsers } = useSelector((state) => state.employees);
    const { employees } = useSelector((state) => state.employees);

    const [newLinkId, setNewLinkId] = useState();
    const [linkedUser, setLinkedUser] = useState();
    const [changed, setChanged] = useState(false);

    // Get test users for link
    useEffect(() => {
        dispatch(fetchTestUsersForLink());
    }, []);

    // Get linked user name
    useEffect(() => {
        if (selectedEmployee?.isLinked) {
            setLinkedUser(employees.data?.find((x) => x.userId === selectedEmployee?.linkedUserId));
        }
    }, []);

    // Put Link status callback
    useEffect(() => {
        if (linkUsers.status === "success") {
            dispatch(resetEmployeeStatus());
            dispatch(setAlertStatus(true))
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            handleClose();
        } else if (linkUsers.status === "failed") {
            dispatch(setAlertStatus(true))
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}`));
            dispatch(setAlertSeverity("error"));
        };
    }, [linkUsers.status]);

    // Put Unlink status callback
    useEffect(() => {
        if (unlinkUsers.status === "success") {
            dispatch(resetEmployeeStatus());
            dispatch(setAlertStatus(true))
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.SAVE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            handleClose();
        } else if (unlinkUsers.status === "failed") {
            dispatch(setAlertStatus(true))
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}`));
            dispatch(setAlertSeverity("error"));
        };
    }, [unlinkUsers.status]);

    // Keep track of changed state 
    useEffect(() => {
        if (newLinkId !== null) {
            setChanged(true);
        } else {
            setChanged(false);
        };
    }, [newLinkId]);

    // Link click function
    const handleLinkClick = () => {
        dispatch(putLinkUsers({ userId: selectedEmployee?.userId, testUserId: newLinkId }))
    };

    // Unlink click function
    const handleUnlinkClick = () => {
        dispatch(putUnlinkUsers(selectedEmployee?.userId))
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <ContentLoader
                    successCondition={testUsers.status === "success"}
                    errorCondition={testUsers.status === "failed"}
                    content={
                        <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                            <Stack spacing={1}>
                                <Stack>
                                    <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.LINK2}</Typography>
                                    <Typography variant="subtitle1" sx={{ ...PageHeader, color: 'primary.light' }}>{selectedEmployee.fullname}</Typography>
                                </Stack>
                                {selectedEmployee?.isLinked && (
                                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                        <Typography>{resourceFinal[appLanguage].words.LINKED}: {linkedUser?.fullname}</Typography>
                                        <IconButton onClick={handleUnlinkClick}><LinkOffIcon color="error" /></IconButton>
                                    </Stack>
                                )}
                                <AutocompleteSelector
                                    label={`${resourceFinal[appLanguage].words.TEST} ${resourceFinal[appLanguage].words.USERS}`}
                                    options={testUsers.data ?? []}
                                    value={null}
                                    onChange={(val) => {
                                        setNewLinkId(val?.userId);
                                    }}
                                    getOptionLabel={(option) => option.fullname}
                                    isOptionEqualToValue={(option, value) => option?.userId === value?.userId}
                                    required
                                    disableClearable
                                />
                                <ButtonGroup sx={{ justifyContent: 'center' }}>
                                    <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                                    <Button disabled={!changed} variant="outlined" color="primary" onClick={handleLinkClick}>{resourceFinal[appLanguage].words.SAVE}</Button>
                                </ButtonGroup>
                            </Stack>
                        </Scrollbars>
                    } />
            </Card>
        </Box >
    );
}
